import { useTranslation } from 'react-i18next'

import { RankingBarsPlot } from '@digital-twins/deck-ui/plots/ranking-bars'
import { formatLabel } from '@/lib/utils/formatting'
import { useRankingBarsLabels } from '@/features/studies/report/page/components/views/analysis/useRankingBarsLabels'

import { FigurePanelSections, FigureViewProps } from './figureViewShared'

import { RankData, RankSeries } from '@digital-twins/slide-schemas/generated/plot-types'

/**
 * `ranking-bars` on the survey tab (plots plan I): the kind's own plot
 * component at its card size, the one the summary card and quick-study draw.
 * Shape only: values, dense ranks, the winner, the tie, the decimals and the
 * scale are Python's; the items are drawn in the order Python sent them. The
 * chrome is in the viewer's UI language (owner decision 5). Item names print
 * as stamped; `isLegacy` (an analyst chart stored before its plot) keeps
 * today's `formatLabel` recasing (Wave 7 S4).
 *
 * A plot that carries `series` (a per-concept read, `figures.py`
 * `_ranking_plots`) draws each series, one titled section per concept when it
 * carries several, as the multi-select view draws its lanes (`ChoiceTotal`).
 */
export function RankingBarsFigure(props: FigureViewProps) {
  const { panels, isCut, isLegacy = false } = props

  const { t } = useTranslation('app')
  const labels = useRankingBarsLabels()

  return (
    <div data-testid="figure-ranking-bars">
      <FigurePanelSections
        panels={panels}
        isCut={isCut}
        testIdPrefix="figure-ranking-bars"
        renderPanel={(panel, index) => {
          const data = panel.plot.data as unknown as RankData
          const prefix = isCut ? `figure-ranking-bars-group-${index}` : 'figure-ranking-bars'
          const series = data.series ?? []
          const drawPlot = (drawn: RankSeries | undefined, testIdPrefix: string) => (
            <RankingBarsPlot
              data={{ ...data, labels: { ...data.labels, ...labels } }}
              size="card"
              seriesId={drawn?.id}
              // No stamped winner (an analyst chart stored before the plot): every bar primary.
              highlight={(drawn ?? data).topIds?.length ? 'top' : 'none'}
              // Stamped names print as Python wrote them; only a legacy read keeps today's recasing.
              formatItemLabel={isLegacy ? formatLabel : undefined}
              emptyLabel={t('studyReport.noRankingData')}
              testIdPrefix={testIdPrefix}
            />
          )
          if (series.length <= 1) {
            return drawPlot(series[0], `${prefix}-plot`)
          }
          return (
            <div className="space-y-8">
              {series.map((lane, laneIndex) => (
                <section key={lane.id} className="space-y-3" data-testid={`${prefix}-lane-${laneIndex}`}>
                  <div className="text-sm font-semibold text-foreground" data-testid={`${prefix}-lane-${laneIndex}-title`}>
                    {lane.label ?? t('studyReport.questionCard.laneFallbackTitle', { n: laneIndex + 1 })}
                  </div>
                  {drawPlot(lane, `${prefix}-lane-${laneIndex}-plot`)}
                </section>
              ))}
            </div>
          )
        }}
      />
    </div>
  )
}
